import { memo, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ConfirmPopup from '@components/ConfirmPopup'
import CustomPopover from '@components/CustomPopover'
import SpinnerWithFakeProgress from '@components/SpinnerWithFakeProgress'
import { SearchRequestBody } from '@modules/search/hooks/useSearchProfiles'
import shortlistApi from '@modules/shortlist/api'
import AddNewShortlistSection from '@modules/shortlist/components/AddNewShortlistSection'
import {
  AddIconCircle,
  AddNewShortlistFooterContainer,
  LineDivider,
} from '@modules/shortlist/components/ShortlistProfileButton/index.styles'
import { useShortlists } from '@modules/shortlist/hooks/useShortlistMap'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Typography } from '@mui/material'
import { useNotification } from '@shared/hooks/useNotification'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { AddToShorltistLink } from '../ResultsSection/index.styles'

interface AddProspectsToShortlistPopoverProps {
  resultCount: number
  popoverAnchor: Nullable<HTMLElement>
  onClose: () => void
  searchBody?: SearchRequestBody
}

const AddProspectsToShortlistPopover = ({
  resultCount,
  popoverAnchor,
  onClose,
  searchBody,
}: AddProspectsToShortlistPopoverProps) => {
  const [isAddNewShortlistVisible, setIsAddNewShortlistVisible] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false)
  const [addedToShortlists, setAddedToShortlists] = useState<Record<string, string>>({})
  const [addingToShortlists, setAddingToShortlists] = useState<Record<string, boolean>>({})
  const { t } = useTranslation()
  const { showNotification } = useNotification()
  const queryClient = useQueryClient()
  const { shortlists, isUniqueName, canAddShortlists } = useShortlists()

  const fakeProgressTimeoutId = useRef<number | undefined>()

  const handleShortlistProfilesChanged = (shortlistId?: number, importId?: number) => {
    if (shortlistId) {
      setAddedToShortlists(stateAddedToShortlists => ({
        ...stateAddedToShortlists,
        [shortlistId]: importId,
      }))
    }

    queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })
    queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key] })
  }

  const handleAddToShortlistError = (shortlistId: number) => {
    if (shortlistId) {
      setAddingToShortlists(stateAddingToShortlists => ({
        ...stateAddingToShortlists,
        [shortlistId]: false,
      }))
    }
    window.clearTimeout(fakeProgressTimeoutId.current)
    showNotification(t(translationKeys.FAILED_TO_SAVE_PROSPECTS_TO_SEARCH), 'error')
  }

  const handleUndoSuccess = (shortlistId: number) => {
    setAddedToShortlists(stateAddedToShortlists => ({
      ...stateAddedToShortlists,
      [shortlistId]: undefined,
    }))

    window.setTimeout(() => handleShortlistProfilesChanged(), 1000 * Math.ceil(resultCount / 100) + 1)
  }

  const { mutate: undoAddToShortlistProfiles } = useMutation({
    mutationFn: shortlistApi.undoAddProfilesToShortlists.fn,
    onSuccess: (_, data) => {
      handleUndoSuccess(data.shortlistId)
    },
    onError: () => {
      window.clearTimeout(fakeProgressTimeoutId.current)
      showNotification(t(translationKeys.FAILED_TO_UNDO_SAVE_PROSPECTS_TO_SEARCH_TITLE), 'error')
    },
  })

  const { mutate: updateShortlistProfilesByQuery } = useMutation({
    mutationFn: shortlistApi.updateShortlistProfileByQuery.fn,
    onSuccess: (response, data) => {
      handleShortlistProfilesChanged(data.id, response.importId)
    },
    onError: (_, data) => {
      handleAddToShortlistError(data.id)
    },
  })

  const handleUndo = (shortlistId: number) => {
    setAddingToShortlists(stateAddingToShortlists => ({
      ...stateAddingToShortlists,
      [shortlistId]: true,
    }))

    undoAddToShortlistProfiles({
      shortlistId,
      importId: addedToShortlists[shortlistId] as string,
    })

    fakeProgressTimeoutId.current = window.setTimeout(() => {
      setAddingToShortlists(stateAddingToShortlists => ({
        ...stateAddingToShortlists,
        [shortlistId]: false,
      }))

      showNotification('', 'success', t(translationKeys.SUCCESSFULLY_UNDO_SAVE_PROSPECTS_TO_SEARCH_TITLE))
    }, getRandomSaveTimeInMs())
  }

  const getRandomSaveTimeInMs = () => (Math.floor(Math.random() * 5) + Math.ceil(resultCount / 100)) * 1000

  const handleAddToShortlist = (shortlistId: number) => {
    setAddingToShortlists(stateAddingToShortlists => ({
      ...stateAddingToShortlists,
      [shortlistId]: true,
    }))

    updateShortlistProfilesByQuery({
      id: shortlistId,
      searchBody,
    })

    fakeProgressTimeoutId.current = window.setTimeout(() => {
      setAddingToShortlists(stateAddingToShortlists => ({
        ...stateAddingToShortlists,
        [shortlistId]: false,
      }))

      showNotification(
        t(translationKeys.SUCCESSFULLY_SAVED_PROSPECTS_TO_SEARCH_SUBTEXT),
        'success',
        t(translationKeys.SUCCESSFULLY_SAVED_PROSPECTS_TO_SEARCH_TITLE),
      )

      // Need this in order to wait out the real adding time, as we have no indication, adding 1 extra second per 100 items added
      window.setTimeout(() => handleShortlistProfilesChanged(), 1000 * (1 + Math.ceil(resultCount / 100)))
    }, getRandomSaveTimeInMs())
  }

  const handleClose = useCallback(() => {
    if (hasChanges) {
      setShowConfirmationPopup(true)
    } else {
      onClose()
    }
  }, [hasChanges])

  const getShortlistButtonState = (shortlistId: number) => {
    if (addingToShortlists[shortlistId])
      return (
        <Box marginRight="4px">
          {' '}
          <SpinnerWithFakeProgress />
        </Box>
      )
    if (addedToShortlists[shortlistId])
      return (
        <Box display="flex" gap="8px" alignItems="center">
          <Typography variant="body1">{t(translationKeys.ADDED)}</Typography>
          <AddToShorltistLink onClick={() => handleUndo(shortlistId)}>{t(translationKeys.UNDO)}</AddToShorltistLink>
        </Box>
      )
    return <AddToShorltistLink onClick={() => handleAddToShortlist(shortlistId)}>{t(translationKeys.ADD)}</AddToShorltistLink>
  }

  const handleNewShortlistListAdded = (id: number) => {
    updateShortlistProfilesByQuery({
      id,
      searchBody,
    })

    setAddedToShortlists({
      ...addedToShortlists,
      [id]: true,
    })
    showNotification('', 'success', t(translationKeys.SUCCESSFULLY_SAVED_ALL_PROSPECTS_TO_SEARCH_TITLE))
    setHasChanges(false)
    setIsAddNewShortlistVisible(false)
    // Need this in order to wait out the real adding time, as we have no indication, adding 10 seconds of delay
    window.setTimeout(() => handleShortlistProfilesChanged(), 1000 * 10)
  }

  return (
    <CustomPopover
      id="add_search_prospects_to_shortlist_popover"
      anchor={popoverAnchor}
      onClose={() => handleClose()}
      setAnchor={() => handleClose()}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        marginTop: '10px',
      }}
    >
      <Box width="300px" padding="20px" display="flex" flexDirection="column" gap="15px">
        <CloseIcon
          onClick={() => handleClose()}
          sx={{ position: 'absolute', right: '5px', top: '5px', width: '20px', height: '20px', cursor: 'pointer' }}
        />
        <Typography width="240px" variant="h2">
          {t(translationKeys.ADD_ALL_PROSPECTS_TO_SHORTLIST_POPOVER_TITLE, { count: resultCount })}
        </Typography>
        <Box display="flex" gap="5px" marginBottom="-10px">
          <InfoOutlinedIcon
            sx={{
              width: '16px',
              height: '16px',
            }}
          />
          <Typography fontSize="12px" maxWidth="225px" variant="body1">
            {t(translationKeys.ADD_ALL_PROSPECTS_TO_SHORTLIST_SUBTEXT)}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            paddingTop="10px"
            paddingBottom="10px"
            marginBottom="-10px"
            flexDirection="column"
            gap="10px"
            maxHeight="210px"
            overflow="hidden auto"
          >
            {shortlists.map(sh => (
              <Box display="flex" margin="-1px 0 -1px 0" justifyContent="space-between" alignItems="center" minHeight="36.5px">
                <Typography variant="body1">{sh.name}</Typography>
                {getShortlistButtonState(sh.id)}
              </Box>
            ))}
          </Box>
          {canAddShortlists && (
            <>
              <LineDivider />
              {!isAddNewShortlistVisible && !!shortlists.length && (
                <AddNewShortlistFooterContainer onClick={() => setIsAddNewShortlistVisible(true)}>
                  <AddIconCircle>
                    <AddIcon color="primary" fontSize="medium" />
                  </AddIconCircle>
                  <Box ml="10px">
                    <Typography variant="body1">{t(translationKeys.ADD_YOUR_CUSTOM_LIST)}</Typography>
                  </Box>
                </AddNewShortlistFooterContainer>
              )}
              {(isAddNewShortlistVisible || !shortlists?.length) && canAddShortlists && (
                <AddNewShortlistSection
                  isUniqueName={isUniqueName}
                  shortlists={shortlists}
                  onSuccess={shortlist => handleNewShortlistListAdded(shortlist?.id as number)}
                  onChange={(name: string) => setHasChanges(!!name?.length)}
                  onCancel={() => {
                    setHasChanges(false)
                    setIsAddNewShortlistVisible(false)
                    onClose()
                    return false
                  }}
                />
              )}
            </>
          )}
        </Box>
      </Box>

      {showConfirmationPopup && (
        <ConfirmPopup
          id="shortlist-profile-button-confirm-exit-popup"
          width={294}
          contentWidth={180}
          isOpen={showConfirmationPopup}
          title={t(translationKeys.UNSAVED_SETTINGS)}
          text={t(translationKeys.YOU_HAVENOT_SAVED_CHANGES)}
          cancelBtnText={t(translationKeys.CONTINUE_EDITING)}
          confirmBtnText={t(translationKeys.CLOSE_ANYWAY)}
          onCancel={() => setShowConfirmationPopup(false)}
          onConfirm={() => {
            onClose()
            setShowConfirmationPopup(false)
          }}
        />
      )}
    </CustomPopover>
  )
}

export default memo(AddProspectsToShortlistPopover)
